import type { PartialDeep } from 'type-fest';

import { isObject } from '@repo/utils/type.utils';

import type { PackageMeasurement, assetTypes } from './asset.types.ts';
import type {
    SalesPriceCouponInfo,
    SalesPriceCouponSummary,
    SalesPriceDiscount,
    SalesPriceDiscountSummary,
    SalesPriceSavings,
} from './sales-price.types.ts';

export const isQuotation = (data: unknown): data is QuoteAsset => {
    return isObject(data) && 'assetDetails' in data && data.assetType === 'quote';
};

export const isQuotationAssetDetails = (data: unknown): data is QuoteAssetDetails => {
    return isObject(data) && 'quotationId' in data;
};

export interface QuoteAsset {
    assetDetails: PartialDeep<QuoteAssetDetails>;
    assetType: typeof assetTypes.quote;
}

export interface QuoteAssetDetails {
    buCode: string;
    quotationId: string;
    countryCode: string;
    createdAt: string;
    createdBy: string;
    validTill: string;
    status: string;
    customerInfo: CustomerInfo;
    customerIdentifier?: CustomerInfo;
    metaData: MetaData;
    lastModifiedAt: string;
    cartData?: CartData;
}

export interface QuotationAttachment {
    fileReference: string;
    fileType: string;
    originalFileName: string;
    uploadedDate: string;
    isNew: boolean;
}

export interface CustomerInfo {
    id: string;
    customerType?: string;
    accountType?: string;
}

export interface MetaData {
    totalPrice: TotalPrice;
    imageUrls: string[];
    orderCreationDate: string;
    quotationExpiryDate: string;
    followUpDateWithCustomer: string;
    numberOfPackages: number;
    totalItemCount: number;
}

export interface TotalPrice {
    priceExclTax: number;
    taxDetails: any[];
    Currency: string;
    priceInclTax: number;
    type: string;
    currencyCode: string;
}

export interface QuotationMessage {
    messageText?: string;
    messageType?: string;
    createDateTime?: string;
    createdUserId?: string;
}

export interface QuotationDrawing {
    drawingName?: string;
    imageUrl?: string;
    drawingId?: string;
    drawingIdSource?: string;
    drawingUrlType?: string;
    drawingUrl?: string;
}

export interface CartData {
    orderMethodOveriden: boolean;
    id: string;
    items: Item[];
    total: Total;
    totalItemCount: number;
    canCreateOrder: boolean;
    isModifyOrderinProgress: boolean;
    order: Order;
    addLoading: boolean;
    sessionAlias?: string;
    salesPriceData: SalesPriceData;
    isQuotation: boolean;
    addedItemId: string;
    currency: string;
    customerMeetingPoint?: CustomerMeetingPoint;
    paymentType: string;
    discounts: any[];
    orderMode: string;
    isTransportDeskMode: boolean;
    hideSaveShoppingList: boolean;
    hasServices: boolean;
    bookedServiceProposal: BookedServiceProposal;
    questionnaires: Questionnaires;
    serviceManager: ServiceManager;
    drawings: QuotationDrawing[];
    attachments: QuotationAttachment[];
    groupCounter: number;
    showStockLoading: boolean;
    comments: any[];
    islatestPriceUpdated: boolean;
    reservedItems: any[];
    isomFullServeErrors: any[];
    draftOrderId?: string;
    quotationId?: string;
    isCompact: boolean;
    customerInformation: CustomerInformation;
    cancelOrderList: any[];
    isDraft: boolean;
    totalFamilyPrice: TotalFamilyPrice13;
    totalPackages: number;
    totalWeight: number;
    totalFullWeight: TotalFullWeight;
    weightUnit: string;
    hasItemsPicked: boolean;
    customerOrderHistory: CustomerOrderHistory;
    _persist: Persist;
    orderItems: OrderItem[];
    customer: Customer2;
    proposal: Proposal;
    orderType: string;
    orderStatus: string;
    buCode: string;
    type: string;
    orderCreationDate: string;
    messages: QuotationMessage[];
}

export interface Item {
    id: number;
    retailItem: RetailItem;
    quantity: number;
    price: Price3;
    totalPrice?: TotalPrice4;
    totalFamilyPrice: TotalFamilyPrice2;
    itemType: string;
    childItems: Item[];
    isCombination: boolean;
    itemId: string;
    isAssemblyRequired: boolean;
    handedOverToTdDesk: boolean;
    isReferenceItem?: boolean;
    isGroup: boolean;
    isTextLine: boolean;
    availableStockToSell: number;
    isProduct: boolean;
    name: string;
    itemUnitCode: string;
    isMetric: boolean;
    numberOfPackages: number;
    itemDescription: string;
    packageWeight: number;
    paNumber: string;
    professionalAssemblyTime: number;
    predictedAssemblyTime: number;
    imageUrl: string;
    totalQuantity: number;
    canCreateOrder: boolean;
    parentId: any;
    seqNo: number;
    soSalesItemNo: number;
    unitPrice: UnitPrice[];
    subtotals: Subtotals;
    isExternalPickingLocation: boolean;
    isLocked: boolean;
}

export interface RetailItem {
    name: string;
    itemId: string;
    itemType: string;
    itemUnitCode: string;
    isMetric: boolean;
    numberOfPackages: number;
    itemDescription: string;
    packageWeight: number;
    packageMeasurements?: PackageMeasurement[];
    paNumber: string;
    professionalAssemblyTime: number;
    predictedAssemblyTime: number;
    price: Price;
    familyPrice: FamilyPrice;
    totalPrice: TotalPrice2;
    totalFamilyPrice: TotalFamilyPrice;
    imageUrl: string;
    storeInfo: StoreInfo;
    availableStockToSell: number;
    isAssemblyRequired: boolean;
}

export interface Price {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail[];
    type: string;
}

export interface TaxDetail {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface FamilyPrice {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail2[];
    type: string;
}

export interface TaxDetail2 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalPrice2 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail3[];
    type: string;
}

export interface TaxDetail3 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalFamilyPrice {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail4[];
    type: string;
}

export interface TaxDetail4 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface StoreInfo {
    isFullServe: boolean;
    isSelfServe: boolean;
    isExternalFullServe: boolean;
    handOverLocation: string;
    isAvailableInStore: boolean;
    isExternalPickingLocation: boolean;
    itemId: string;
    itemType: string;
    paNumber: string;
    pickingLocation: string;
    salesLocations: SalesLocation[];
    salesMethodCode: string;
    salesRestrictions: SalesRestrictions;
    stockAvailabilities: StockAvailabilities;
    servicePrices: ServicePrice[];
    price: Price2;
    availableStockToSell: number;
    isFoodItem: boolean;
    totalPrice: TotalPrice3;
}

export interface SalesLocation {
    IsHomeBase: boolean;
    IsPrimary: boolean;
    SelfServeSalesLocations: SelfServeSalesLocations;
}

export interface SelfServeSalesLocations {
    Aisle: string;
    Bin: string;
}

export interface SalesRestrictions {
    CanSellLocal: boolean;
    CanSellRemote: boolean;
    ShouldSellLocal: boolean;
    ShouldSellRemote: boolean;
    IsOrderStopped: boolean;
    IsSaleStopped: boolean;
}

export interface StockAvailabilities {
    Elevated: number;
    Total: number;
    ToSell: number;
}

export interface ServicePrice {
    serviceProductId: string;
    currencyCode: string;
    type: string;
    priceExclTax: number;
    priceInclTax: number;
    indicativeTotalTax: number;
}

export interface Price2 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail5[];
    type: string;
    indicativeTax: number;
}

export interface TaxDetail5 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalPrice3 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail6[];
    type: string;
    indicativeTax: number;
}

export interface TaxDetail6 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface Price3 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
    taxDetails: any[];
}

export interface TotalPrice4 {
    currencyCode?: string;
    priceInclTax?: number;
    priceExclTax?: number;
    type?: string;
}

export interface TotalFamilyPrice2 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
}

export interface UnitPrice {
    changeReason: string;
    currentPrice: CurrentPrice;
    discountedPrice: DiscountedPrice;
    type: string;
}

export interface CurrentPrice {
    indicativeTax: number;
    priceExclTax: number;
    priceInclTax: number;
}

export interface DiscountedPrice {
    indicativeTax: number;
    priceExclTax: number;
    priceInclTax: number;
}

export interface Subtotals {
    prices: Price4[];
}

export interface Price4 {
    viewType: string;
    priceType: string;
    inclSavings: InclSavings;
    exclSavings: ExclSavings;
}

export interface InclSavings {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail7[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail7 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface ExclSavings {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail8[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail8 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface Total {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
    taxDetails: any[];
    Currency: string;
}

export interface Order {
    shouldUpdate: boolean;
    modifiedOrder: boolean;
}

export interface SalesPriceData {
    couponInfo?: SalesPriceCouponInfo[];
    currencyCode: string;
    deliveryServices: DeliveryService[];
    discounts: SalesPriceDiscount[];
    orderSavings: any[];
    orderSubtotal: OrderSubtotal[];
    orderSummary: OrderSummary;
    priceViewType: string;
    providedServices: ProvidedService[];
    savings: SalesPriceSavings;
    solutions: Solution[];
    timezoneId: string;
    vat: Vat;
}

export interface OrderSummary {
    summary?: Summary[];
}

export interface Summary {
    discountSummary?: Array<SalesPriceDiscountSummary | SalesPriceCouponSummary>;
    exclSavings: ExclSavings2;
    inclSavings: InclSavings2;
    savings?: SalesPriceSavings;
    viewType: string;
}

export interface InclSavings2 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail9[];
    taxSummary: TaxSummary[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail9 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface TaxSummary {
    taxType: string;
    totalTaxAmount: number;
}

export interface ExclSavings2 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail10[];
    taxSummary: TaxSummary2[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail10 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface TaxSummary2 {
    taxType: string;
    totalTaxAmount: number;
}

export interface OrderSubtotal {
    viewType: string;
    product?: Product;
    delivery: Delivery;
    service?: Service;
}

export interface Product {
    inclSavings: InclSavings3;
    exclSavings: ExclSavings3;
}

export interface InclSavings3 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail11[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail11 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface ExclSavings3 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail12[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail12 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface Delivery {
    inclSavings: InclSavings4;
    exclSavings: ExclSavings4;
}

export interface InclSavings4 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail13[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail13 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface ExclSavings4 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail14[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail14 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface Service {
    inclSavings: InclSavings5;
    exclSavings: ExclSavings5;
}

export interface InclSavings5 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail15[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail15 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface ExclSavings5 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail16[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail16 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface Vat {
    amount: number;
    priceInclTax: number;
    currencyCode: string;
}

export interface DeliveryService {
    lineId: string;
    deliveryMethod: string;
    deliveryMethodId: string;
    deliveryPrice?: DeliveryPrice[];
    deliveryType: string;
    itemLineReferences: ItemLineReference[];
    solutionReference: string;
    programmaticPriceOverride: ProgrammaticPriceOverride;
    serviceArea: ServiceArea;
    taxationInfo: string;
    deliveryOption: string;
}

export interface DeliveryPrice {
    viewType: string;
    inclSavings: InclSavings6;
    exclSavings: ExclSavings6;
    priceType: string;
    savings?: QuotationDeliveryPriceSaving[];
}

interface QuotationDeliveryPriceSaving {
    amount: number;
    id: string;
    texts: Array<{
        langCode: string;
        name: string;
    }>;
    description: string;
    discountType: string;
    type: string;
    validFromDate: string;
    validToDate: string;
    validFromDateUTC: string;
    validToDateUTC: string;
    discountDetails: {
        count: number;
        value: number;
        type: string;
    };
    discountLevel: string;
    code: string;
}

export interface InclSavings6 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail17[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail17 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface ExclSavings6 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail18[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail18 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface ItemLineReference {
    lineId: string;
    quantity: number;
}

export interface ProgrammaticPriceOverride {
    priceInclTax: number;
}

export interface ServiceArea {
    countryCode: string;
    city: string;
    addressLine1: string;
    zipCode: string;
}

export interface Solution {
    solutionId: string;
    price: Price5[];
    programmaticPriceOverride: ProgrammaticPriceOverride2;
    currencyCode: string;
}

export interface Price5 {
    viewType: string;
    inclSavings: InclSavings7;
    exclSavings: ExclSavings7;
    priceType: string;
}

export interface InclSavings7 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail19[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail19 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface ExclSavings7 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail20[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail20 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface ProgrammaticPriceOverride2 {
    priceInclTax: number;
}

export interface ProvidedService {
    itemLineReferences: ItemLineReference2[];
    lineId: string;
    programmaticPriceOverride: ProgrammaticPriceOverride3;
    priceCalculation: PriceCalculation;
    servicePrice: ServicePrice2[];
    serviceProductId: string;
    serviceItemNo: string;
    serviceItemType: string;
    payToProvider: boolean;
    includedInOrderSubtotalAndSummary: boolean;
    serviceArea: ServiceArea2;
    taxationInfo: string;
}

export interface ItemLineReference2 {
    lineId: string;
    quantity: number;
}

export interface ProgrammaticPriceOverride3 {
    priceInclTax: number;
}

export interface PriceCalculation {
    calculationType: string;
}

export interface ServicePrice2 {
    viewType: string;
    inclSavings: InclSavings8;
    exclSavings: ExclSavings8;
    priceType: string;
}

export interface InclSavings8 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail21[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail21 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface ExclSavings8 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail22[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail22 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface ServiceArea2 {
    countryCode: string;
    city: string;
    addressLine1: string;
    zipCode: string;
}

export interface CustomerMeetingPoint {
    id: string;
    name: string;
}

export interface BookedServiceProposal {
    hasProvidedService: boolean;
    hasDelivery: boolean;
    bookedServices: BookedService[];
    error: Error;
    zipCode: string;
    activeZipCode: string;
    isCcdService: boolean;
    isLcdService: boolean;
    selectedPickservice: any;
    isMissingDeliveryForNewItems: boolean;
    hasServices: boolean;
}

export interface BookedService {
    customerServiceType: string;
    status?: string;
    statusTranslated?: string;
    subDeliveries?: SubDelivery[];
    price: Price11;
    discountPrice?: DiscountPrice2;
    solutionId?: string;
    deliveryArrangementsId?: string;
    zipCode: string;
    fulfillmentMethod?: string;
    fulfillmentType?: string;
    hasNoStockDelivery?: boolean;
    isLocked?: boolean;
    id: string;
    serviceId?: string;
    serviceName: string;
    isCentralDelivery?: boolean;
    canDeleteService: boolean;
    canModifyTimeWindow: boolean;
    canModifyPrice: boolean;
    referenceItems: ReferenceItem2[];
    unavailableItems?: any[];
    selectedCartItems: SelectedCartItem[];
    hasValidPrice?: boolean;
    capacityNeeded?: any;
    capacityUnit?: string;
    priceCalculationValue?: number;
    priceCalculation?: number;
    priceCalculationType?: string;
    priceCalculationTypeTranslated?: string;
    priceCalculationUnit?: string;
    priceCalculationUnitTranslated?: string;
    serviceProviderId?: string;
    serviceProviderName?: string;
    serviceProductId?: string;
    serviceType?: string;
    serviceProviderPaymentList?: ServiceProviderPaymentList[];
    serviceProviderPreferredPayment?: string;
    timeWindows?: TimeWindow3[];
    alternateServiceProviders?: AlternateServiceProvider[];
    isDelivery?: boolean;
    isProvidedService?: boolean;
    isPickingService?: boolean;
    payToIkea?: boolean;
    hasAlternatePaymentOption?: boolean;
    timeWindow?: TimeWindow4;
    providerName?: string;
    serviceOption?: ServiceOption;
    serviceItemNo?: string;
    serviceItemId?: string;
    itemRequiredForService?: boolean;
    serviceConfiguration?: ServiceConfiguration2;
    customerServiceTypeTranslated?: string;
    lineId?: number;
}

export interface SubDelivery {
    price: Price6 | Price56;
    discountPrice?: DiscountPrice;
    customerServiceType: string;
    id: string;
    hasNoStockDelivery: boolean;
    solutionId: string;
    deliveryArrangementsId: string;
    deliveryId: string;
    serviceItemId: string;
    timeWindows?: TimeWindow[];
    timeWindow?: TimeWindow2;
    referenceItems: ReferenceItem[];
    capabilityOptions: any[];
    isLocked: boolean;
    canDeleteService: boolean;
    canModifyTimeWindow: boolean;
    canModifyPrice: boolean;
    isCentralDelivery: boolean;
    serviceName: string;
    status: string;
    statusTranslated: string;
    unitOfMeasure: string;
    translationId: string;
    isRangeOfDays: boolean;
    lineId?: number;
    hasPickupPoint?: boolean;
    pickupPoint?: PickupPoint;
}

export interface PickupPoint {
    addressLine1?: string;
    addressLine2?: string;
    addressLine3?: string;
    addressLine4?: string;
    latitude?: string;
    longitude?: string;
    city?: string;
    country?: string;
    zipCode?: string;
    distance?: string;
    id?: string;
    identifier?: string;
    lsc?: string;
    openingHoursMonTime?: string;
    openingHoursTueTime?: string;
    openingHoursWedTime?: string;
    openingHoursThuTime?: string;
    openingHoursFriTime?: string;
    openingHoursSatTime?: string;
    openingHoursSunTime?: string;
    name?: string;
    state?: string;
    price?: Price56;
}

export interface Price6 {
    amount: number;
    priceInclTax: number;
    priceExclTax: number;
    currencyCode: string;
    taxDetails: TaxDetail23[];
    discountPrice: number;
}

export interface TaxDetail23 {
    taxType: string;
    taxCode: string;
    taxAmount: number;
    taxableAmount: number;
    taxPercentage: number;
}

export interface DiscountPrice {
    amount: number;
    priceInclTax: number;
    priceExclTax: number;
    currencyCode: string;
    taxDetails: TaxDetail24[];
    discountPrice: number;
}

export interface TaxDetail24 {
    taxType: string;
    taxCode: string;
    taxAmount: number;
    taxableAmount: number;
    taxPercentage: number;
}

export interface TimeWindow {
    hasDeliveryExceptionalQty: string;
    transferringUnit: TransferringUnit;
    fulfillingUnit: FulfillingUnit;
    capacityAvailable: boolean;
    fromDateTime: string;
    toDateTime: string;
    node: string;
    resourcePoolId: string;
    date: string;
    id: string;
    resourceId: string;
    timeWindowId: string;
    nodeId: string;
    serviceProviderId: string;
    serviceProviderName: string;
    timeZoneId: string;
    timeZone: string;
    isExceptionalVolume: boolean;
    isExceptionalQty: boolean;
    itemLines: ItemLine[];
    paymentCutOffDateTime: string;
    tspID: string;
    tspName: string;
    startTime: string;
    endTime: string;
    fromDateTimeUtc: string;
    toDateTimeUtc: string;
}

export interface TransferringUnit {
    type: string;
    code: string;
    name: string;
    phoneNo: string;
}

export interface FulfillingUnit {
    type: string;
    code: string;
}

export interface ItemLine {
    isExceptionalQty: string;
    shipNode: string;
    id: string;
}

export interface TimeWindow2 {
    hasDeliveryExceptionalQty: string;
    transferringUnit: TransferringUnit2;
    fulfillingUnit: FulfillingUnit2;
    capacityAvailable: boolean;
    fromDateTime: string;
    toDateTime: string;
    node: string;
    resourcePoolId: string;
    date: string;
    id: string;
    resourceId: string;
    timeWindowId: string;
    nodeId: string;
    serviceProviderId: string;
    serviceProviderName: string;
    timeZoneId: string;
    timeZone: string;
    isExceptionalVolume: boolean;
    isExceptionalQty: boolean;
    itemLines: ItemLine2[];
    paymentCutOffDateTime: string;
    tspID: string;
    tspName: string;
    startTime: string;
    endTime: string;
    fromDateTimeUtc: string;
    toDateTimeUtc: string;
}

export interface TransferringUnit2 {
    type: string;
    code: string;
    name: string;
    phoneNo: string;
}

export interface FulfillingUnit2 {
    type: string;
    code: string;
}

export interface ItemLine2 {
    isExceptionalQty: string;
    shipNode: string;
    id: string;
}

export interface ReferenceItem {
    itemType: string;
    itemNo: string;
    itemName: string;
    id: number;
    quantity: number;
    itemUnitCode: string;
    refId: number;
    soSalesItemNo?: number;
    orderLineId: number;
    retailItem: RetailItem2;
    price: Price9;
    totalPrice: TotalPrice7;
    totalFamilyPrice: TotalFamilyPrice4;
    childItems: any[];
    isCombination: boolean;
    itemId: string;
    isAssemblyRequired: boolean;
    handedOverToTdDesk: boolean;
    isGroup: boolean;
    isTextLine: boolean;
    availableStockToSell: number;
    isProduct: boolean;
    name: string;
    isMetric: boolean;
    numberOfPackages: number;
    itemDescription: string;
    packageWeight: number;
    paNumber: string;
    professionalAssemblyTime: number;
    predictedAssemblyTime: number;
    imageUrl: string;
    totalQuantity: number;
    canCreateOrder: boolean;
    parentId?: any;
    seqNo: number;
    unitPrice: UnitPrice2[];
    subtotals: Subtotals2;
    isExternalPickingLocation: boolean;
    isLocked?: boolean;
    unitofMeasure: string;
    lineId?: string;
    timeWindowLineItem: TimeWindowLineItem;
    availableQty: number;
    isKitchenUnit?: boolean;
    isFullServe?: boolean;
    isExternalFullServe?: boolean;
    taxationInfo?: string;
    isAvailable?: boolean;
}

export interface RetailItem2 {
    name: string;
    itemId: string;
    itemType: string;
    itemUnitCode: string;
    isMetric: boolean;
    numberOfPackages: number;
    itemDescription: string;
    packageWeight: number;
    paNumber: string;
    professionalAssemblyTime: number;
    predictedAssemblyTime: number;
    price: Price7;
    familyPrice: FamilyPrice2;
    totalPrice: TotalPrice5;
    totalFamilyPrice: TotalFamilyPrice3;
    imageUrl: string;
    storeInfo: StoreInfo2;
    availableStockToSell: number;
    isAssemblyRequired: boolean;
    isKitchenUnit?: boolean;
    totalStock?: number;
}

export interface Price7 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail25[];
    type: string;
}

export interface TaxDetail25 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface FamilyPrice2 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail26[];
    type: string;
}

export interface TaxDetail26 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalPrice5 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail27[];
    type: string;
}

export interface TaxDetail27 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalFamilyPrice3 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail28[];
    type: string;
}

export interface TaxDetail28 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface StoreInfo2 {
    isFullServe: boolean;
    isSelfServe: boolean;
    isExternalFullServe: boolean;
    handOverLocation: string;
    isAvailableInStore: boolean;
    isExternalPickingLocation: boolean;
    itemId: string;
    itemType: string;
    paNumber: string;
    pickingLocation: string;
    salesLocations: SalesLocation2[] | SalesLocation13[];
    salesMethodCode: string;
    salesRestrictions: SalesRestrictions2;
    stockAvailabilities: StockAvailabilities2;
    servicePrices?: ServicePrice3[];
    price: Price8;
    availableStockToSell: number;
    isFoodItem: boolean;
    totalPrice: TotalPrice6;
}

export interface SalesLocation2 {
    IsHomeBase: boolean;
    IsPrimary: boolean;
    SelfServeSalesLocations: SelfServeSalesLocations2;
}

export interface SelfServeSalesLocations2 {
    Aisle: string;
    Bin: string;
}

export interface SalesRestrictions2 {
    CanSellLocal: boolean;
    CanSellRemote: boolean;
    ShouldSellLocal: boolean;
    ShouldSellRemote: boolean;
    IsOrderStopped: boolean;
    IsSaleStopped: boolean;
}

export interface StockAvailabilities2 {
    Elevated: number;
    Total: number;
    ToSell: number;
}

export interface ServicePrice3 {
    serviceProductId: string;
    currencyCode: string;
    type: string;
    priceExclTax: number;
    priceInclTax: number;
    indicativeTotalTax: number;
}

export interface Price8 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail29[];
    type: string;
    indicativeTax: number;
}

export interface TaxDetail29 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalPrice6 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail30[];
    type: string;
    indicativeTax: number;
}

export interface TaxDetail30 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface Price9 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
    taxDetails: any[];
}

export interface TotalPrice7 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
}

export interface TotalFamilyPrice4 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
}

export interface UnitPrice2 {
    changeReason: string;
    currentPrice: CurrentPrice2;
    discountedPrice: DiscountedPrice2;
    type: string;
}

export interface CurrentPrice2 {
    indicativeTax: number;
    priceExclTax: number;
    priceInclTax: number;
}

export interface DiscountedPrice2 {
    indicativeTax: number;
    priceExclTax: number;
    priceInclTax: number;
}

export interface Subtotals2 {
    prices: Price10[];
}

export interface Price10 {
    viewType: string;
    priceType: string;
    inclSavings: InclSavings9;
    exclSavings: ExclSavings9;
}

export interface InclSavings9 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail31[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail31 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface ExclSavings9 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail32[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail32 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface TimeWindowLineItem {
    isExceptionalQty: string;
    shipNode: string;
    id: string;
}

export interface Price11 {
    amount?: number;
    priceInclTax: number;
    priceExclTax: number;
    currencyCode: string;
    taxDetails: TaxDetail33[];
    discountPrice: number;
}

export interface TaxDetail33 {
    taxType: string;
    taxCode: string;
    taxAmount: number;
    taxableAmount: number;
    taxPercentage: number;
}

export interface DiscountPrice2 {
    amount?: number;
    priceInclTax: number;
    priceExclTax: number;
    currencyCode: string;
    taxDetails: TaxDetail34[];
    discountPrice: number;
}

export interface TaxDetail34 {
    taxType: string;
    taxCode: string;
    taxAmount: number;
    taxableAmount: number;
    taxPercentage: number;
}

export interface ReferenceItem2 {
    itemType: string;
    itemNo: string;
    itemName: string;
    id: number;
    quantity: number;
    itemUnitCode: string;
    refId: number;
    soSalesItemNo: number;
    orderLineId: number;
    retailItem: RetailItem3;
    price: Price14;
    totalPrice: TotalPrice10;
    totalFamilyPrice: TotalFamilyPrice6;
    childItems: any[];
    isCombination: boolean;
    itemId: string;
    isAssemblyRequired: boolean;
    handedOverToTdDesk: boolean;
    isGroup: boolean;
    isTextLine: boolean;
    availableStockToSell: number;
    isProduct: boolean;
    name: string;
    isMetric: boolean;
    numberOfPackages: number;
    itemDescription: string;
    packageWeight: number;
    paNumber: string;
    professionalAssemblyTime: number;
    predictedAssemblyTime: number;
    imageUrl: string;
    totalQuantity: number;
    canCreateOrder: boolean;
    parentId: any;
    seqNo: number;
    unitPrice: UnitPrice3[];
    subtotals: Subtotals3;
    isExternalPickingLocation: boolean;
    isLocked?: boolean;
    unitofMeasure: string;
    lineId: any;
    timeWindowLineItem?: TimeWindowLineItem2;
    availableQty?: number;
}

export interface RetailItem3 {
    name: string;
    itemId: string;
    itemType: string;
    itemUnitCode: string;
    isMetric: boolean;
    numberOfPackages: number;
    itemDescription: string;
    packageWeight: number;
    paNumber: string;
    professionalAssemblyTime: number;
    predictedAssemblyTime: number;
    price: Price12;
    familyPrice: FamilyPrice3;
    totalPrice: TotalPrice8;
    totalFamilyPrice: TotalFamilyPrice5;
    imageUrl: string;
    storeInfo: StoreInfo3;
    availableStockToSell: number;
    isAssemblyRequired: boolean;
}

export interface Price12 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail35[];
    type: string;
}

export interface TaxDetail35 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface FamilyPrice3 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail36[];
    type: string;
}

export interface TaxDetail36 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalPrice8 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail37[];
    type: string;
}

export interface TaxDetail37 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalFamilyPrice5 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail38[];
    type: string;
}

export interface TaxDetail38 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface StoreInfo3 {
    isFullServe: boolean;
    isSelfServe: boolean;
    isExternalFullServe: boolean;
    handOverLocation: string;
    isAvailableInStore: boolean;
    isExternalPickingLocation: boolean;
    itemId: string;
    itemType: string;
    paNumber: string;
    pickingLocation: string;
    salesLocations: SalesLocation3[];
    salesMethodCode: string;
    salesRestrictions: SalesRestrictions3;
    stockAvailabilities: StockAvailabilities3;
    servicePrices: ServicePrice4[];
    price: Price13;
    availableStockToSell: number;
    isFoodItem: boolean;
    totalPrice: TotalPrice9;
}

export interface SalesLocation3 {
    IsHomeBase: boolean;
    IsPrimary: boolean;
    SelfServeSalesLocations: SelfServeSalesLocations3;
}

export interface SelfServeSalesLocations3 {
    Aisle: string;
    Bin: string;
}

export interface SalesRestrictions3 {
    CanSellLocal: boolean;
    CanSellRemote: boolean;
    ShouldSellLocal: boolean;
    ShouldSellRemote: boolean;
    IsOrderStopped: boolean;
    IsSaleStopped: boolean;
}

export interface StockAvailabilities3 {
    Elevated: number;
    Total: number;
    ToSell: number;
}

export interface ServicePrice4 {
    serviceProductId: string;
    currencyCode: string;
    type: string;
    priceExclTax: number;
    priceInclTax: number;
    indicativeTotalTax: number;
}

export interface Price13 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail39[];
    type: string;
    indicativeTax: number;
}

export interface TaxDetail39 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalPrice9 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail40[];
    type: string;
    indicativeTax: number;
}

export interface TaxDetail40 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface Price14 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
    taxDetails: any[];
}

export interface TotalPrice10 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
}

export interface TotalFamilyPrice6 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
}

export interface UnitPrice3 {
    changeReason: string;
    currentPrice: CurrentPrice3;
    discountedPrice: DiscountedPrice3;
    type: string;
}

export interface CurrentPrice3 {
    indicativeTax: number;
    priceExclTax: number;
    priceInclTax: number;
}

export interface DiscountedPrice3 {
    indicativeTax: number;
    priceExclTax: number;
    priceInclTax: number;
}

export interface Subtotals3 {
    prices: Price15[];
}

export interface Price15 {
    viewType: string;
    priceType: string;
    inclSavings: InclSavings10;
    exclSavings: ExclSavings10;
}

export interface InclSavings10 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail41[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail41 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface ExclSavings10 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail42[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail42 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface TimeWindowLineItem2 {
    isExceptionalQty: string;
    shipNode: string;
    id: string;
}

export interface SelectedCartItem {
    itemType: string;
    itemNo: string;
    itemName: string;
    id: number;
    quantity: number;
    itemUnitCode: string;
    refId: number;
    soSalesItemNo: number;
    orderLineId: number;
    retailItem: RetailItem4;
    price: Price18;
    totalPrice: TotalPrice13;
    totalFamilyPrice: TotalFamilyPrice8;
    childItems: any[];
    isCombination: boolean;
    itemId: string;
    isAssemblyRequired: boolean;
    handedOverToTdDesk: boolean;
    isGroup: boolean;
    isTextLine: boolean;
    availableStockToSell: number;
    isProduct: boolean;
    name: string;
    isMetric: boolean;
    numberOfPackages: number;
    itemDescription: string;
    packageWeight: number;
    paNumber: string;
    professionalAssemblyTime: number;
    predictedAssemblyTime: number;
    imageUrl: string;
    totalQuantity: number;
    canCreateOrder: boolean;
    parentId: any;
    seqNo: number;
    unitPrice: UnitPrice4[];
    subtotals: Subtotals4;
    isExternalPickingLocation: boolean;
    isLocked?: boolean;
    unitofMeasure: string;
    lineId: any;
    timeWindowLineItem?: TimeWindowLineItem3;
    availableQty?: number;
}

export interface RetailItem4 {
    name: string;
    itemId: string;
    itemType: string;
    itemUnitCode: string;
    isMetric: boolean;
    numberOfPackages: number;
    itemDescription: string;
    packageWeight: number;
    paNumber: string;
    professionalAssemblyTime: number;
    predictedAssemblyTime: number;
    price: Price16;
    familyPrice: FamilyPrice4;
    totalPrice: TotalPrice11;
    totalFamilyPrice: TotalFamilyPrice7;
    imageUrl: string;
    storeInfo: StoreInfo4;
    availableStockToSell: number;
    isAssemblyRequired: boolean;
}

export interface Price16 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail43[];
    type: string;
}

export interface TaxDetail43 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface FamilyPrice4 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail44[];
    type: string;
}

export interface TaxDetail44 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalPrice11 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail45[];
    type: string;
}

export interface TaxDetail45 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalFamilyPrice7 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail46[];
    type: string;
}

export interface TaxDetail46 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface StoreInfo4 {
    isFullServe: boolean;
    isSelfServe: boolean;
    isExternalFullServe: boolean;
    handOverLocation: string;
    isAvailableInStore: boolean;
    isExternalPickingLocation: boolean;
    itemId: string;
    itemType: string;
    paNumber: string;
    pickingLocation: string;
    salesLocations: SalesLocation4[];
    salesMethodCode: string;
    salesRestrictions: SalesRestrictions4;
    stockAvailabilities: StockAvailabilities4;
    servicePrices: ServicePrice5[];
    price: Price17;
    availableStockToSell: number;
    isFoodItem: boolean;
    totalPrice: TotalPrice12;
}

export interface SalesLocation4 {
    IsHomeBase: boolean;
    IsPrimary: boolean;
    SelfServeSalesLocations: SelfServeSalesLocations4;
}

export interface SelfServeSalesLocations4 {
    Aisle: string;
    Bin: string;
}

export interface SalesRestrictions4 {
    CanSellLocal: boolean;
    CanSellRemote: boolean;
    ShouldSellLocal: boolean;
    ShouldSellRemote: boolean;
    IsOrderStopped: boolean;
    IsSaleStopped: boolean;
}

export interface StockAvailabilities4 {
    Elevated: number;
    Total: number;
    ToSell: number;
}

export interface ServicePrice5 {
    serviceProductId: string;
    currencyCode: string;
    type: string;
    priceExclTax: number;
    priceInclTax: number;
    indicativeTotalTax: number;
}

export interface Price17 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail47[];
    type: string;
    indicativeTax: number;
}

export interface TaxDetail47 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalPrice12 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail48[];
    type: string;
    indicativeTax: number;
}

export interface TaxDetail48 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface Price18 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
    taxDetails: any[];
}

export interface TotalPrice13 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
}

export interface TotalFamilyPrice8 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
}

export interface UnitPrice4 {
    changeReason: string;
    currentPrice: CurrentPrice4;
    discountedPrice: DiscountedPrice4;
    type: string;
}

export interface CurrentPrice4 {
    indicativeTax: number;
    priceExclTax: number;
    priceInclTax: number;
}

export interface DiscountedPrice4 {
    indicativeTax: number;
    priceExclTax: number;
    priceInclTax: number;
}

export interface Subtotals4 {
    prices: Price19[];
}

export interface Price19 {
    viewType: string;
    priceType: string;
    inclSavings: InclSavings11;
    exclSavings: ExclSavings11;
}

export interface InclSavings11 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail49[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail49 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface ExclSavings11 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail50[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail50 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface TimeWindowLineItem3 {
    isExceptionalQty: string;
    shipNode: string;
    id: string;
}

export interface ServiceProviderPaymentList {
    isPreferred: boolean;
    paymentTo: string;
}

export interface TimeWindow3 {
    id: string;
    fromDateTime: string;
    toDateTime: string;
    timeZone: string;
    capacityAllocated: number;
    sequence: number;
}

export interface AlternateServiceProvider {
    serviceProviderId: string;
    serviceProviderName: string;
    preferredPaymentTo: string;
    serviceProviderPaymentList: ServiceProviderPaymentList2[];
}

export interface ServiceProviderPaymentList2 {
    paymentTo: string;
    isPreferred: boolean;
}

export interface TimeWindow4 {
    id: string;
    fromDateTime: string;
    toDateTime: string;
    timeZone: string;
    capacityAllocated: number;
    sequence: number;
    paymentCutOffDateTime?: string;
}

export interface ServiceOption {
    compatibleServices: any[];
    transportDependent: boolean;
    integrator: string;
    afterSales: boolean;
    serviceName: string;
    serviceProductId: string;
    serviceType: string;
    serviceTypeTranslated: string;
    serviceConfiguration: ServiceConfiguration;
    basePrice: number;
    unitPrice: number;
    assemblyMinimumPrice: number;
    price: Price20;
    capacityUnit: string;
    capacityUnitTranslated: string;
    capacityNeeded: any;
    priceCalculationValue: number;
    itemRequiredForService: boolean;
    priceCalculationUnit: string;
    priceCalculationUnitTranslated: string;
    priceCalculationType: string;
    priceCalculationTypeTranslated: string;
    serviceItemNo: string;
    surveyConnected: boolean;
    hasConfigured: boolean;
    isDelivery: boolean;
    isProvidedService: boolean;
    isSelected: boolean;
    id: string;
    isPickingService: boolean;
    cartItems: CartItem[];
    selectedCartItems: SelectedCartItem2[];
    zipCode: string;
    showInputView: boolean;
}

export interface ServiceConfiguration {
    basePrice: number;
    basePriceType: string;
    unitPrice: number;
    currencyCode: string;
}

export interface Price20 {
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail51[];
    currencyCode: string;
}

export interface TaxDetail51 {
    taxType: string;
    taxCode: string;
    taxAmount: number;
    taxableAmount: number;
    taxPercentage: number;
}

export interface CartItem {
    id: number;
    retailItem: RetailItem5;
    quantity: number;
    price: Price23;
    totalPrice: TotalPrice16;
    totalFamilyPrice: TotalFamilyPrice10;
    itemType: string;
    childItems: any[];
    isCombination: boolean;
    itemId: string;
    isAssemblyRequired: boolean;
    handedOverToTdDesk: boolean;
    isGroup: boolean;
    isTextLine: boolean;
    availableStockToSell: number;
    isProduct: boolean;
    name: string;
    itemUnitCode: string;
    isMetric: boolean;
    numberOfPackages: number;
    itemDescription: string;
    packageWeight: number;
    paNumber: string;
    professionalAssemblyTime: number;
    predictedAssemblyTime: number;
    imageUrl: string;
    totalQuantity: number;
    canCreateOrder: boolean;
    parentId: any;
    seqNo: number;
    soSalesItemNo: number;
    unitPrice: UnitPrice5[];
    subtotals: Subtotals5;
    isExternalPickingLocation: boolean;
    isLocked?: boolean;
}

export interface RetailItem5 {
    name: string;
    itemId: string;
    itemType: string;
    itemUnitCode: string;
    isMetric: boolean;
    numberOfPackages: number;
    itemDescription: string;
    packageWeight: number;
    paNumber: string;
    professionalAssemblyTime: number;
    predictedAssemblyTime: number;
    price: Price21;
    familyPrice: FamilyPrice5;
    totalPrice: TotalPrice14;
    totalFamilyPrice: TotalFamilyPrice9;
    imageUrl: string;
    storeInfo: StoreInfo5;
    availableStockToSell: number;
    isAssemblyRequired: boolean;
}

export interface Price21 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail52[];
    type: string;
}

export interface TaxDetail52 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface FamilyPrice5 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail53[];
    type: string;
}

export interface TaxDetail53 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalPrice14 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail54[];
    type: string;
}

export interface TaxDetail54 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalFamilyPrice9 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail55[];
    type: string;
}

export interface TaxDetail55 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface StoreInfo5 {
    isFullServe: boolean;
    isSelfServe: boolean;
    isExternalFullServe: boolean;
    handOverLocation: string;
    isAvailableInStore: boolean;
    isExternalPickingLocation: boolean;
    itemId: string;
    itemType: string;
    paNumber: string;
    pickingLocation: string;
    salesLocations: SalesLocation5[];
    salesMethodCode: string;
    salesRestrictions: SalesRestrictions5;
    stockAvailabilities: StockAvailabilities5;
    servicePrices: ServicePrice6[];
    price: Price22;
    availableStockToSell: number;
    isFoodItem: boolean;
    totalPrice: TotalPrice15;
}

export interface SalesLocation5 {
    IsHomeBase: boolean;
    IsPrimary: boolean;
    SelfServeSalesLocations: SelfServeSalesLocations5;
}

export interface SelfServeSalesLocations5 {
    Aisle: string;
    Bin: string;
}

export interface SalesRestrictions5 {
    CanSellLocal: boolean;
    CanSellRemote: boolean;
    ShouldSellLocal: boolean;
    ShouldSellRemote: boolean;
    IsOrderStopped: boolean;
    IsSaleStopped: boolean;
}

export interface StockAvailabilities5 {
    Elevated: number;
    Total: number;
    ToSell: number;
}

export interface ServicePrice6 {
    serviceProductId: string;
    currencyCode: string;
    type: string;
    priceExclTax: number;
    priceInclTax: number;
    indicativeTotalTax: number;
}

export interface Price22 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail56[];
    type: string;
    indicativeTax: number;
}

export interface TaxDetail56 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalPrice15 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail57[];
    type: string;
    indicativeTax: number;
}

export interface TaxDetail57 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface Price23 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
    taxDetails: any[];
}

export interface TotalPrice16 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
}

export interface TotalFamilyPrice10 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
}

export interface UnitPrice5 {
    changeReason: string;
    currentPrice: CurrentPrice5;
    discountedPrice: DiscountedPrice5;
    type: string;
}

export interface CurrentPrice5 {
    indicativeTax: number;
    priceExclTax: number;
    priceInclTax: number;
}

export interface DiscountedPrice5 {
    indicativeTax: number;
    priceExclTax: number;
    priceInclTax: number;
}

export interface Subtotals5 {
    prices: Price24[];
}

export interface Price24 {
    viewType: string;
    priceType: string;
    inclSavings: InclSavings12;
    exclSavings: ExclSavings12;
}

export interface InclSavings12 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail58[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail58 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface ExclSavings12 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail59[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail59 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface SelectedCartItem2 {
    itemType: string;
    itemNo: string;
    itemName: string;
    id: number;
    quantity: number;
    itemUnitCode: string;
    refId: number;
    soSalesItemNo: number;
    orderLineId: number;
    retailItem: RetailItem6;
    price: Price27;
    totalPrice: TotalPrice19;
    totalFamilyPrice: TotalFamilyPrice12;
    childItems: any[];
    isCombination: boolean;
    itemId: string;
    isAssemblyRequired: boolean;
    handedOverToTdDesk: boolean;
    isGroup: boolean;
    isTextLine: boolean;
    availableStockToSell: number;
    isProduct: boolean;
    name: string;
    isMetric: boolean;
    numberOfPackages: number;
    itemDescription: string;
    packageWeight: number;
    paNumber: string;
    professionalAssemblyTime: number;
    predictedAssemblyTime: number;
    imageUrl: string;
    totalQuantity: number;
    canCreateOrder: boolean;
    parentId: any;
    seqNo: number;
    unitPrice: UnitPrice6[];
    subtotals: Subtotals6;
    isExternalPickingLocation: boolean;
    isLocked?: boolean;
    unitofMeasure: string;
    lineId: number;
}

export interface RetailItem6 {
    name: string;
    itemId: string;
    itemType: string;
    itemUnitCode: string;
    isMetric: boolean;
    numberOfPackages: number;
    itemDescription: string;
    packageWeight: number;
    paNumber: string;
    professionalAssemblyTime: number;
    predictedAssemblyTime: number;
    price: Price25;
    familyPrice: FamilyPrice6;
    totalPrice: TotalPrice17;
    totalFamilyPrice: TotalFamilyPrice11;
    imageUrl: string;
    storeInfo: StoreInfo6;
    availableStockToSell: number;
    isAssemblyRequired: boolean;
}

export interface Price25 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail60[];
    type: string;
}

export interface TaxDetail60 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface FamilyPrice6 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail61[];
    type: string;
}

export interface TaxDetail61 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalPrice17 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail62[];
    type: string;
}

export interface TaxDetail62 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalFamilyPrice11 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail63[];
    type: string;
}

export interface TaxDetail63 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface StoreInfo6 {
    isFullServe: boolean;
    isSelfServe: boolean;
    isExternalFullServe: boolean;
    handOverLocation: string;
    isAvailableInStore: boolean;
    isExternalPickingLocation: boolean;
    itemId: string;
    itemType: string;
    paNumber: string;
    pickingLocation: string;
    salesLocations: SalesLocation6[];
    salesMethodCode: string;
    salesRestrictions: SalesRestrictions6;
    stockAvailabilities: StockAvailabilities6;
    servicePrices: ServicePrice7[];
    price: Price26;
    availableStockToSell: number;
    isFoodItem: boolean;
    totalPrice: TotalPrice18;
}

export interface SalesLocation6 {
    IsHomeBase: boolean;
    IsPrimary: boolean;
    SelfServeSalesLocations: SelfServeSalesLocations6;
}

export interface SelfServeSalesLocations6 {
    Aisle: string;
    Bin: string;
}

export interface SalesRestrictions6 {
    CanSellLocal: boolean;
    CanSellRemote: boolean;
    ShouldSellLocal: boolean;
    ShouldSellRemote: boolean;
    IsOrderStopped: boolean;
    IsSaleStopped: boolean;
}

export interface StockAvailabilities6 {
    Elevated: number;
    Total: number;
    ToSell: number;
}

export interface ServicePrice7 {
    serviceProductId: string;
    currencyCode: string;
    type: string;
    priceExclTax: number;
    priceInclTax: number;
    indicativeTotalTax: number;
}

export interface Price26 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail64[];
    type: string;
    indicativeTax: number;
}

export interface TaxDetail64 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalPrice18 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail65[];
    type: string;
    indicativeTax: number;
}

export interface TaxDetail65 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface Price27 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
    taxDetails: any[];
}

export interface TotalPrice19 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
}

export interface TotalFamilyPrice12 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
}

export interface UnitPrice6 {
    changeReason: string;
    currentPrice: CurrentPrice6;
    discountedPrice: DiscountedPrice6;
    type: string;
}

export interface CurrentPrice6 {
    indicativeTax: number;
    priceExclTax: number;
    priceInclTax: number;
}

export interface DiscountedPrice6 {
    indicativeTax: number;
    priceExclTax: number;
    priceInclTax: number;
}

export interface Subtotals6 {
    prices: Price28[];
}

export interface Price28 {
    viewType: string;
    priceType: string;
    inclSavings: InclSavings13;
    exclSavings: ExclSavings13;
}

export interface InclSavings13 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail66[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail66 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface ExclSavings13 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail67[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail67 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface ServiceConfiguration2 {
    basePrice: number;
    basePriceType: string;
    unitPrice: number;
    currencyCode: string;
}

export interface Error {}

export interface Questionnaires {
    questionNeeded: boolean;
    deliveryQuestions: DeliveryQuestion[];
    serviceQuestions: any[];
    questionaireAnswers: QuestionaireAnswer[];
    selectedDeliveryQuestionnaireType: string;
}

export interface DeliveryQuestion {
    serviceReference: string;
    isMandatory: boolean;
    translatedReference: string;
    reference: string;
    options: Option[];
    isValid: boolean;
    requireFreeText: boolean;
    ref: Ref;
}

export interface Option {
    requireFreeText: boolean;
    value: string;
}

export interface Ref {
    current: any;
}

export interface QuestionaireAnswer {
    reference: string;
    option: string;
    freeText: string;
}

export interface ServiceManager {}

export interface CustomerInformation {
    hasCustomer: boolean;
    customer: Customer;
    customerBilling: CustomerBilling;
    icmCustomer: IcmCustomer;
}

export interface Customer {
    isNewDeliveryAddress: boolean;
    isAddedBillAddress: boolean;
    isAddedDeliveryAddress: boolean;
    noOfFamilyCards: number;
    noOfBusinessCards: number;
    vatPayer: string;
}

export interface CustomerBilling {
    isNewDeliveryAddress: boolean;
    isAddedBillAddress: boolean;
    isAddedDeliveryAddress: boolean;
    noOfFamilyCards: number;
    noOfBusinessCards: number;
    vatPayer: string;
}

export interface IcmCustomer {}

export interface TotalFamilyPrice13 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
    taxDetails: any[];
}

export interface TotalFullWeight {
    metric: number;
    imperial: number;
}

export interface CustomerOrderHistory {
    orderHistory: any[];
    success: boolean;
}

export interface Persist {
    version: number;
    rehydrated: boolean;
}

export interface OrderItem {
    id: number;
    retailItem: RetailItem7;
    quantity: number;
    price: Price31;
    totalPrice: TotalPrice22;
    totalFamilyPrice: TotalFamilyPrice15;
    itemType: string;
    childItems: any[];
    isCombination: boolean;
    itemId: string;
    isAssemblyRequired: boolean;
    handedOverToTdDesk: boolean;
    isGroup: boolean;
    isTextLine: boolean;
    availableStockToSell: number;
    isProduct: boolean;
    name: string;
    itemUnitCode: string;
    isMetric: boolean;
    numberOfPackages: number;
    itemDescription: string;
    packageWeight: number;
    paNumber: string;
    professionalAssemblyTime: number;
    predictedAssemblyTime: number;
    imageUrl: string;
    totalQuantity: number;
    canCreateOrder: boolean;
    parentId: any;
    seqNo: number;
    soSalesItemNo: number;
    unitPrice: UnitPrice7[];
    subtotals: Subtotals7;
    isExternalPickingLocation: boolean;
    isLocked?: boolean;
}

export interface RetailItem7 {
    name: string;
    itemId: string;
    itemType: string;
    itemUnitCode: string;
    isMetric: boolean;
    numberOfPackages: number;
    itemDescription: string;
    packageWeight: number;
    paNumber: string;
    professionalAssemblyTime: number;
    predictedAssemblyTime: number;
    price: Price29;
    familyPrice: FamilyPrice7;
    totalPrice: TotalPrice20;
    totalFamilyPrice: TotalFamilyPrice14;
    imageUrl: string;
    storeInfo: StoreInfo7;
    availableStockToSell: number;
    isAssemblyRequired: boolean;
}

export interface Price29 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail68[];
    type: string;
}

export interface TaxDetail68 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface FamilyPrice7 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail69[];
    type: string;
}

export interface TaxDetail69 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalPrice20 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail70[];
    type: string;
}

export interface TaxDetail70 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalFamilyPrice14 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail71[];
    type: string;
}

export interface TaxDetail71 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface StoreInfo7 {
    isFullServe: boolean;
    isSelfServe: boolean;
    isExternalFullServe: boolean;
    handOverLocation: string;
    isAvailableInStore: boolean;
    isExternalPickingLocation: boolean;
    itemId: string;
    itemType: string;
    paNumber: string;
    pickingLocation: string;
    salesLocations: SalesLocation7[];
    salesMethodCode: string;
    salesRestrictions: SalesRestrictions7;
    stockAvailabilities: StockAvailabilities7;
    servicePrices: ServicePrice8[];
    price: Price30;
    availableStockToSell: number;
    isFoodItem: boolean;
    totalPrice: TotalPrice21;
}

export interface SalesLocation7 {
    IsHomeBase: boolean;
    IsPrimary: boolean;
    SelfServeSalesLocations: SelfServeSalesLocations7;
}

export interface SelfServeSalesLocations7 {
    Aisle: string;
    Bin: string;
}

export interface SalesRestrictions7 {
    CanSellLocal: boolean;
    CanSellRemote: boolean;
    ShouldSellLocal: boolean;
    ShouldSellRemote: boolean;
    IsOrderStopped: boolean;
    IsSaleStopped: boolean;
}

export interface StockAvailabilities7 {
    Elevated: number;
    Total: number;
    ToSell: number;
}

export interface ServicePrice8 {
    serviceProductId: string;
    currencyCode: string;
    type: string;
    priceExclTax: number;
    priceInclTax: number;
    indicativeTotalTax: number;
}

export interface Price30 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail72[];
    type: string;
    indicativeTax: number;
}

export interface TaxDetail72 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalPrice21 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail73[];
    type: string;
    indicativeTax: number;
}

export interface TaxDetail73 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface Price31 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
    taxDetails: any[];
}

export interface TotalPrice22 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
}

export interface TotalFamilyPrice15 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
}

export interface UnitPrice7 {
    changeReason: string;
    currentPrice: CurrentPrice7;
    discountedPrice: DiscountedPrice7;
    type: string;
}

export interface CurrentPrice7 {
    indicativeTax: number;
    priceExclTax: number;
    priceInclTax: number;
}

export interface DiscountedPrice7 {
    indicativeTax: number;
    priceExclTax: number;
    priceInclTax: number;
}

export interface Subtotals7 {
    prices: Price32[];
}

export interface Price32 {
    viewType: string;
    priceType: string;
    inclSavings: InclSavings14;
    exclSavings: ExclSavings14;
}

export interface InclSavings14 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail74[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail74 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface ExclSavings14 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail75[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail75 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface Customer2 {
    ikeaFamilyNumber: string;
    addressId: string;
    addressContextType: string;
    partyId: string;
    firstName: string;
    lastName: string;
    genderCode: string;
    email: string;
    mobilePhone: string;
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
    careOf: string;
    zipCode: string;
    city: string;
    stateCode: string;
    countryCode: string;
    preferredLocale: string;
    language: string;
    isBusinessCustomer: boolean;
    vatPayer: string;
    id: string;
    partyUId: string;
    fullName: string;
}

export interface Proposal {
    hasProvidedService: boolean;
    hasDelivery: boolean;
    bookedServices: BookedService2[];
    error: Error2;
    zipCode: string;
    activeZipCode: string;
    isCcdService: boolean;
    isLcdService: boolean;
    selectedPickservice: any;
    isMissingDeliveryForNewItems: boolean;
    hasServices: boolean;
}

export interface BookedService2 {
    customerServiceType: string;
    status?: string;
    statusTranslated?: string;
    subDeliveries?: SubDelivery2[];
    price: Price38;
    discountPrice: DiscountPrice4;
    solutionId?: string;
    deliveryArrangementsId?: string;
    zipCode: string;
    fulfillmentMethod?: string;
    fulfillmentType?: string;
    hasNoStockDelivery?: boolean;
    isLocked?: boolean;
    id: string;
    serviceId?: string;
    serviceName: string;
    isCentralDelivery?: boolean;
    canDeleteService: boolean;
    canModifyTimeWindow: boolean;
    canModifyPrice: boolean;
    referenceItems: ReferenceItem4[];
    unavailableItems?: any[];
    selectedCartItems: SelectedCartItem3[];
    hasValidPrice?: boolean;
    capacityNeeded?: any;
    capacityUnit?: string;
    priceCalculationValue?: number;
    priceCalculation?: number;
    priceCalculationType?: string;
    priceCalculationTypeTranslated?: string;
    priceCalculationUnit?: string;
    priceCalculationUnitTranslated?: string;
    serviceProviderId?: string;
    serviceProviderName?: string;
    serviceProductId?: string;
    serviceType?: string;
    serviceProviderPaymentList?: ServiceProviderPaymentList3[];
    serviceProviderPreferredPayment?: string;
    timeWindows?: TimeWindow7[];
    alternateServiceProviders?: AlternateServiceProvider2[];
    isDelivery?: boolean;
    isProvidedService?: boolean;
    isPickingService?: boolean;
    payToIkea?: boolean;
    hasAlternatePaymentOption?: boolean;
    timeWindow?: TimeWindow8;
    providerName?: string;
    serviceOption?: ServiceOption2;
    serviceItemNo?: string;
    serviceItemId?: string;
    itemRequiredForService?: boolean;
    serviceConfiguration?: ServiceConfiguration4;
    customerServiceTypeTranslated?: string;
    lineId?: number;
}

export interface SubDelivery2 {
    price: Price33;
    discountPrice: DiscountPrice3;
    customerServiceType: string;
    id: string;
    hasNoStockDelivery: boolean;
    solutionId: string;
    deliveryArrangementsId: string;
    deliveryId: string;
    serviceItemId: string;
    timeWindows?: TimeWindow5[];
    timeWindow?: TimeWindow6;
    referenceItems: ReferenceItem3[];
    capabilityOptions: any[];
    isLocked?: boolean;
    canDeleteService: boolean;
    canModifyTimeWindow: boolean;
    canModifyPrice: boolean;
    isCentralDelivery: boolean;
    serviceName: string;
    status: string;
    statusTranslated: string;
    unitOfMeasure: string;
    translationId: string;
    isRangeOfDays: boolean;
    lineId: number;
}

export interface Price33 {
    amount: number;
    priceInclTax: number;
    priceExclTax: number;
    currencyCode: string;
    taxDetails: TaxDetail76[];
    discountPrice: number;
}

export interface TaxDetail76 {
    taxType: string;
    taxCode: string;
    taxAmount: number;
    taxableAmount: number;
    taxPercentage: number;
}

export interface DiscountPrice3 {
    amount: number;
    priceInclTax: number;
    priceExclTax: number;
    currencyCode: string;
    taxDetails: TaxDetail77[];
    discountPrice: number;
}

export interface TaxDetail77 {
    taxType: string;
    taxCode: string;
    taxAmount: number;
    taxableAmount: number;
    taxPercentage: number;
}

export interface TimeWindow5 {
    hasDeliveryExceptionalQty: string;
    transferringUnit: TransferringUnit3;
    fulfillingUnit: FulfillingUnit3;
    capacityAvailable: boolean;
    fromDateTime: string;
    toDateTime: string;
    node: string;
    resourcePoolId: string;
    date: string;
    id: string;
    resourceId: string;
    timeWindowId: string;
    nodeId: string;
    serviceProviderId: string;
    serviceProviderName: string;
    timeZoneId: string;
    timeZone: string;
    isExceptionalVolume: boolean;
    isExceptionalQty: boolean;
    itemLines: ItemLine3[];
    paymentCutOffDateTime: string;
    tspID: string;
    tspName: string;
    startTime: string;
    endTime: string;
    fromDateTimeUtc: string;
    toDateTimeUtc: string;
}

export interface TransferringUnit3 {
    type: string;
    code: string;
    name: string;
    phoneNo: string;
}

export interface FulfillingUnit3 {
    type: string;
    code: string;
}

export interface ItemLine3 {
    isExceptionalQty: string;
    shipNode: string;
    id: string;
}

export interface TimeWindow6 {
    hasDeliveryExceptionalQty: string;
    transferringUnit: TransferringUnit4;
    fulfillingUnit: FulfillingUnit4;
    capacityAvailable: boolean;
    fromDateTime: string;
    toDateTime: string;
    node: string;
    resourcePoolId: string;
    date: string;
    id: string;
    resourceId: string;
    timeWindowId: string;
    nodeId: string;
    serviceProviderId: string;
    serviceProviderName: string;
    timeZoneId: string;
    timeZone: string;
    isExceptionalVolume: boolean;
    isExceptionalQty: boolean;
    itemLines: ItemLine4[];
    paymentCutOffDateTime: string;
    tspID: string;
    tspName: string;
    startTime: string;
    endTime: string;
    fromDateTimeUtc: string;
    toDateTimeUtc: string;
}

export interface TransferringUnit4 {
    type: string;
    code: string;
    name: string;
    phoneNo: string;
}

export interface FulfillingUnit4 {
    type: string;
    code: string;
}

export interface ItemLine4 {
    isExceptionalQty: string;
    shipNode: string;
    id: string;
}

export interface ReferenceItem3 {
    itemType: string;
    itemNo: string;
    itemName: string;
    id: number;
    quantity: number;
    itemUnitCode: string;
    refId: number;
    soSalesItemNo: number;
    orderLineId: number;
    retailItem: RetailItem8;
    price: Price36;
    totalPrice: TotalPrice25;
    totalFamilyPrice: TotalFamilyPrice17;
    childItems: any[];
    isCombination: boolean;
    itemId: string;
    isAssemblyRequired: boolean;
    handedOverToTdDesk: boolean;
    isGroup: boolean;
    isTextLine: boolean;
    availableStockToSell: number;
    isProduct: boolean;
    name: string;
    isMetric: boolean;
    numberOfPackages: number;
    itemDescription: string;
    packageWeight: number;
    paNumber: string;
    professionalAssemblyTime: number;
    predictedAssemblyTime: number;
    imageUrl: string;
    totalQuantity: number;
    canCreateOrder: boolean;
    parentId: any;
    seqNo: number;
    unitPrice: UnitPrice8[];
    subtotals: Subtotals8;
    isExternalPickingLocation: boolean;
    isLocked?: boolean;
    unitofMeasure: string;
    lineId: string;
    timeWindowLineItem: TimeWindowLineItem4;
    availableQty: number;
}

export interface RetailItem8 {
    name: string;
    itemId: string;
    itemType: string;
    itemUnitCode: string;
    isMetric: boolean;
    numberOfPackages: number;
    itemDescription: string;
    packageWeight: number;
    paNumber: string;
    professionalAssemblyTime: number;
    predictedAssemblyTime: number;
    price: Price34;
    familyPrice: FamilyPrice8;
    totalPrice: TotalPrice23;
    totalFamilyPrice: TotalFamilyPrice16;
    imageUrl: string;
    storeInfo: StoreInfo8;
    availableStockToSell: number;
    isAssemblyRequired: boolean;
}

export interface Price34 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail78[];
    type: string;
}

export interface TaxDetail78 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface FamilyPrice8 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail79[];
    type: string;
}

export interface TaxDetail79 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalPrice23 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail80[];
    type: string;
}

export interface TaxDetail80 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalFamilyPrice16 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail81[];
    type: string;
}

export interface TaxDetail81 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface StoreInfo8 {
    isFullServe: boolean;
    isSelfServe: boolean;
    isExternalFullServe: boolean;
    handOverLocation: string;
    isAvailableInStore: boolean;
    isExternalPickingLocation: boolean;
    itemId: string;
    itemType: string;
    paNumber: string;
    pickingLocation: string;
    salesLocations: SalesLocation8[];
    salesMethodCode: string;
    salesRestrictions: SalesRestrictions8;
    stockAvailabilities: StockAvailabilities8;
    servicePrices: ServicePrice9[];
    price: Price35;
    availableStockToSell: number;
    isFoodItem: boolean;
    totalPrice: TotalPrice24;
}

export interface SalesLocation8 {
    IsHomeBase: boolean;
    IsPrimary: boolean;
    SelfServeSalesLocations: SelfServeSalesLocations8;
}

export interface SelfServeSalesLocations8 {
    Aisle: string;
    Bin: string;
}

export interface SalesRestrictions8 {
    CanSellLocal: boolean;
    CanSellRemote: boolean;
    ShouldSellLocal: boolean;
    ShouldSellRemote: boolean;
    IsOrderStopped: boolean;
    IsSaleStopped: boolean;
}

export interface StockAvailabilities8 {
    Elevated: number;
    Total: number;
    ToSell: number;
}

export interface ServicePrice9 {
    serviceProductId: string;
    currencyCode: string;
    type: string;
    priceExclTax: number;
    priceInclTax: number;
    indicativeTotalTax: number;
}

export interface Price35 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail82[];
    type: string;
    indicativeTax: number;
}

export interface TaxDetail82 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalPrice24 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail83[];
    type: string;
    indicativeTax: number;
}

export interface TaxDetail83 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface Price36 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
    taxDetails: any[];
}

export interface TotalPrice25 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
}

export interface TotalFamilyPrice17 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
}

export interface UnitPrice8 {
    changeReason: string;
    currentPrice: CurrentPrice8;
    discountedPrice: DiscountedPrice8;
    type: string;
}

export interface CurrentPrice8 {
    indicativeTax: number;
    priceExclTax: number;
    priceInclTax: number;
}

export interface DiscountedPrice8 {
    indicativeTax: number;
    priceExclTax: number;
    priceInclTax: number;
}

export interface Subtotals8 {
    prices: Price37[];
}

export interface Price37 {
    viewType: string;
    priceType: string;
    inclSavings: InclSavings15;
    exclSavings: ExclSavings15;
}

export interface InclSavings15 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail84[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail84 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface ExclSavings15 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail85[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail85 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface TimeWindowLineItem4 {
    isExceptionalQty: string;
    shipNode: string;
    id: string;
}

export interface Price38 {
    amount?: number;
    priceInclTax: number;
    priceExclTax: number;
    currencyCode: string;
    taxDetails: TaxDetail86[];
    discountPrice: number;
}

export interface TaxDetail86 {
    taxType: string;
    taxCode: string;
    taxAmount: number;
    taxableAmount: number;
    taxPercentage: number;
}

export interface DiscountPrice4 {
    amount?: number;
    priceInclTax: number;
    priceExclTax: number;
    currencyCode: string;
    taxDetails: TaxDetail87[];
    discountPrice: number;
}

export interface TaxDetail87 {
    taxType: string;
    taxCode: string;
    taxAmount: number;
    taxableAmount: number;
    taxPercentage: number;
}

export interface ReferenceItem4 {
    itemType: string;
    itemNo: string;
    itemName: string;
    id: number;
    quantity: number;
    itemUnitCode: string;
    refId: number;
    soSalesItemNo: number;
    orderLineId: number;
    retailItem: RetailItem9;
    price: Price41;
    totalPrice: TotalPrice28;
    totalFamilyPrice: TotalFamilyPrice19;
    childItems: any[];
    isCombination: boolean;
    itemId: string;
    isAssemblyRequired: boolean;
    handedOverToTdDesk: boolean;
    isGroup: boolean;
    isTextLine: boolean;
    availableStockToSell: number;
    isProduct: boolean;
    name: string;
    isMetric: boolean;
    numberOfPackages: number;
    itemDescription: string;
    packageWeight: number;
    paNumber: string;
    professionalAssemblyTime: number;
    predictedAssemblyTime: number;
    imageUrl: string;
    totalQuantity: number;
    canCreateOrder: boolean;
    parentId: any;
    seqNo: number;
    unitPrice: UnitPrice9[];
    subtotals: Subtotals9;
    isExternalPickingLocation: boolean;
    isLocked?: boolean;
    unitofMeasure: string;
    lineId: any;
    timeWindowLineItem?: TimeWindowLineItem5;
    availableQty?: number;
}

export interface RetailItem9 {
    name: string;
    itemId: string;
    itemType: string;
    itemUnitCode: string;
    isMetric: boolean;
    numberOfPackages: number;
    itemDescription: string;
    packageWeight: number;
    paNumber: string;
    professionalAssemblyTime: number;
    predictedAssemblyTime: number;
    price: Price39;
    familyPrice: FamilyPrice9;
    totalPrice: TotalPrice26;
    totalFamilyPrice: TotalFamilyPrice18;
    imageUrl: string;
    storeInfo: StoreInfo9;
    availableStockToSell: number;
    isAssemblyRequired: boolean;
}

export interface Price39 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail88[];
    type: string;
}

export interface TaxDetail88 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface FamilyPrice9 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail89[];
    type: string;
}

export interface TaxDetail89 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalPrice26 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail90[];
    type: string;
}

export interface TaxDetail90 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalFamilyPrice18 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail91[];
    type: string;
}

export interface TaxDetail91 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface StoreInfo9 {
    isFullServe: boolean;
    isSelfServe: boolean;
    isExternalFullServe: boolean;
    handOverLocation: string;
    isAvailableInStore: boolean;
    isExternalPickingLocation: boolean;
    itemId: string;
    itemType: string;
    paNumber: string;
    pickingLocation: string;
    salesLocations: SalesLocation9[];
    salesMethodCode: string;
    salesRestrictions: SalesRestrictions9;
    stockAvailabilities: StockAvailabilities9;
    servicePrices: ServicePrice10[];
    price: Price40;
    availableStockToSell: number;
    isFoodItem: boolean;
    totalPrice: TotalPrice27;
}

export interface SalesLocation9 {
    IsHomeBase: boolean;
    IsPrimary: boolean;
    SelfServeSalesLocations: SelfServeSalesLocations9;
}

export interface SelfServeSalesLocations9 {
    Aisle: string;
    Bin: string;
}

export interface SalesRestrictions9 {
    CanSellLocal: boolean;
    CanSellRemote: boolean;
    ShouldSellLocal: boolean;
    ShouldSellRemote: boolean;
    IsOrderStopped: boolean;
    IsSaleStopped: boolean;
}

export interface StockAvailabilities9 {
    Elevated: number;
    Total: number;
    ToSell: number;
}

export interface ServicePrice10 {
    serviceProductId: string;
    currencyCode: string;
    type: string;
    priceExclTax: number;
    priceInclTax: number;
    indicativeTotalTax: number;
}

export interface Price40 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail92[];
    type: string;
    indicativeTax: number;
}

export interface TaxDetail92 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalPrice27 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail93[];
    type: string;
    indicativeTax: number;
}

export interface TaxDetail93 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface Price41 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
    taxDetails: any[];
}

export interface TotalPrice28 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
}

export interface TotalFamilyPrice19 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
}

export interface UnitPrice9 {
    changeReason: string;
    currentPrice: CurrentPrice9;
    discountedPrice: DiscountedPrice9;
    type: string;
}

export interface CurrentPrice9 {
    indicativeTax: number;
    priceExclTax: number;
    priceInclTax: number;
}

export interface DiscountedPrice9 {
    indicativeTax: number;
    priceExclTax: number;
    priceInclTax: number;
}

export interface Subtotals9 {
    prices: Price42[];
}

export interface Price42 {
    viewType: string;
    priceType: string;
    inclSavings: InclSavings16;
    exclSavings: ExclSavings16;
}

export interface InclSavings16 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail94[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail94 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface ExclSavings16 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail95[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail95 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface TimeWindowLineItem5 {
    isExceptionalQty: string;
    shipNode: string;
    id: string;
}

export interface SelectedCartItem3 {
    itemType: string;
    itemNo: string;
    itemName: string;
    id: number;
    quantity: number;
    itemUnitCode: string;
    refId: number;
    soSalesItemNo: number;
    orderLineId: number;
    retailItem: RetailItem10;
    price: Price45;
    totalPrice: TotalPrice31;
    totalFamilyPrice: TotalFamilyPrice21;
    childItems: any[];
    isCombination: boolean;
    itemId: string;
    isAssemblyRequired: boolean;
    handedOverToTdDesk: boolean;
    isGroup: boolean;
    isTextLine: boolean;
    availableStockToSell: number;
    isProduct: boolean;
    name: string;
    isMetric: boolean;
    numberOfPackages: number;
    itemDescription: string;
    packageWeight: number;
    paNumber: string;
    professionalAssemblyTime: number;
    predictedAssemblyTime: number;
    imageUrl: string;
    totalQuantity: number;
    canCreateOrder: boolean;
    parentId: any;
    seqNo: number;
    unitPrice: UnitPrice10[];
    subtotals: Subtotals10;
    isExternalPickingLocation: boolean;
    isLocked?: boolean;
    unitofMeasure: string;
    lineId: any;
    timeWindowLineItem?: TimeWindowLineItem6;
    availableQty?: number;
}

export interface RetailItem10 {
    name: string;
    itemId: string;
    itemType: string;
    itemUnitCode: string;
    isMetric: boolean;
    numberOfPackages: number;
    itemDescription: string;
    packageWeight: number;
    paNumber: string;
    professionalAssemblyTime: number;
    predictedAssemblyTime: number;
    price: Price43;
    familyPrice: FamilyPrice10;
    totalPrice: TotalPrice29;
    totalFamilyPrice: TotalFamilyPrice20;
    imageUrl: string;
    storeInfo: StoreInfo10;
    availableStockToSell: number;
    isAssemblyRequired: boolean;
}

export interface Price43 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail96[];
    type: string;
}

export interface TaxDetail96 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface FamilyPrice10 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail97[];
    type: string;
}

export interface TaxDetail97 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalPrice29 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail98[];
    type: string;
}

export interface TaxDetail98 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalFamilyPrice20 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail99[];
    type: string;
}

export interface TaxDetail99 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface StoreInfo10 {
    isFullServe: boolean;
    isSelfServe: boolean;
    isExternalFullServe: boolean;
    handOverLocation: string;
    isAvailableInStore: boolean;
    isExternalPickingLocation: boolean;
    itemId: string;
    itemType: string;
    paNumber: string;
    pickingLocation: string;
    salesLocations: SalesLocation10[];
    salesMethodCode: string;
    salesRestrictions: SalesRestrictions10;
    stockAvailabilities: StockAvailabilities10;
    servicePrices: ServicePrice11[];
    price: Price44;
    availableStockToSell: number;
    isFoodItem: boolean;
    totalPrice: TotalPrice30;
}

export interface SalesLocation10 {
    IsHomeBase: boolean;
    IsPrimary: boolean;
    SelfServeSalesLocations: SelfServeSalesLocations10;
}

export interface SelfServeSalesLocations10 {
    Aisle: string;
    Bin: string;
}

export interface SalesRestrictions10 {
    CanSellLocal: boolean;
    CanSellRemote: boolean;
    ShouldSellLocal: boolean;
    ShouldSellRemote: boolean;
    IsOrderStopped: boolean;
    IsSaleStopped: boolean;
}

export interface StockAvailabilities10 {
    Elevated: number;
    Total: number;
    ToSell: number;
}

export interface ServicePrice11 {
    serviceProductId: string;
    currencyCode: string;
    type: string;
    priceExclTax: number;
    priceInclTax: number;
    indicativeTotalTax: number;
}

export interface Price44 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail100[];
    type: string;
    indicativeTax: number;
}

export interface TaxDetail100 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalPrice30 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail101[];
    type: string;
    indicativeTax: number;
}

export interface TaxDetail101 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface Price45 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
    taxDetails: any[];
}

export interface TotalPrice31 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
}

export interface TotalFamilyPrice21 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
}

export interface UnitPrice10 {
    changeReason: string;
    currentPrice: CurrentPrice10;
    discountedPrice: DiscountedPrice10;
    type: string;
}

export interface CurrentPrice10 {
    indicativeTax: number;
    priceExclTax: number;
    priceInclTax: number;
}

export interface DiscountedPrice10 {
    indicativeTax: number;
    priceExclTax: number;
    priceInclTax: number;
}

export interface Subtotals10 {
    prices: Price46[];
}

export interface Price46 {
    viewType: string;
    priceType: string;
    inclSavings: InclSavings17;
    exclSavings: ExclSavings17;
}

export interface InclSavings17 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail102[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail102 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface ExclSavings17 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail103[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail103 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface TimeWindowLineItem6 {
    isExceptionalQty: string;
    shipNode: string;
    id: string;
}

export interface ServiceProviderPaymentList3 {
    isPreferred: boolean;
    paymentTo: string;
}

export interface TimeWindow7 {
    id: string;
    fromDateTime: string;
    toDateTime: string;
    timeZone: string;
    capacityAllocated: number;
    sequence: number;
}

export interface AlternateServiceProvider2 {
    serviceProviderId: string;
    serviceProviderName: string;
    preferredPaymentTo: string;
    serviceProviderPaymentList: ServiceProviderPaymentList4[];
}

export interface ServiceProviderPaymentList4 {
    paymentTo: string;
    isPreferred: boolean;
}

export interface TimeWindow8 {
    id: string;
    fromDateTime: string;
    toDateTime: string;
    timeZone: string;
    capacityAllocated: number;
    sequence: number;
}

export interface ServiceOption2 {
    compatibleServices: any[];
    transportDependent: boolean;
    integrator: string;
    afterSales: boolean;
    serviceName: string;
    serviceProductId: string;
    serviceType: string;
    serviceTypeTranslated: string;
    serviceConfiguration: ServiceConfiguration3;
    basePrice: number;
    unitPrice: number;
    assemblyMinimumPrice: number;
    price: Price47;
    capacityUnit: string;
    capacityUnitTranslated: string;
    capacityNeeded: any;
    priceCalculationValue: number;
    itemRequiredForService: boolean;
    priceCalculationUnit: string;
    priceCalculationUnitTranslated: string;
    priceCalculationType: string;
    priceCalculationTypeTranslated: string;
    serviceItemNo: string;
    surveyConnected: boolean;
    hasConfigured: boolean;
    isDelivery: boolean;
    isProvidedService: boolean;
    isSelected: boolean;
    id: string;
    isPickingService: boolean;
    cartItems: CartItem2[];
    selectedCartItems: SelectedCartItem4[];
    zipCode: string;
    showInputView: boolean;
}

export interface ServiceConfiguration3 {
    basePrice: number;
    basePriceType: string;
    unitPrice: number;
    currencyCode: string;
}

export interface Price47 {
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail104[];
    currencyCode: string;
}

export interface TaxDetail104 {
    taxType: string;
    taxCode: string;
    taxAmount: number;
    taxableAmount: number;
    taxPercentage: number;
}

export interface CartItem2 {
    id: number;
    retailItem: RetailItem11;
    quantity: number;
    price: Price50;
    totalPrice: TotalPrice34;
    totalFamilyPrice: TotalFamilyPrice23;
    itemType: string;
    childItems: any[];
    isCombination: boolean;
    itemId: string;
    isAssemblyRequired: boolean;
    handedOverToTdDesk: boolean;
    isGroup: boolean;
    isTextLine: boolean;
    availableStockToSell: number;
    isProduct: boolean;
    name: string;
    itemUnitCode: string;
    isMetric: boolean;
    numberOfPackages: number;
    itemDescription: string;
    packageWeight: number;
    paNumber: string;
    professionalAssemblyTime: number;
    predictedAssemblyTime: number;
    imageUrl: string;
    totalQuantity: number;
    canCreateOrder: boolean;
    parentId: any;
    seqNo: number;
    soSalesItemNo: number;
    unitPrice: UnitPrice11[];
    subtotals: Subtotals11;
    isExternalPickingLocation: boolean;
    isLocked?: boolean;
}

export interface RetailItem11 {
    name: string;
    itemId: string;
    itemType: string;
    itemUnitCode: string;
    isMetric: boolean;
    numberOfPackages: number;
    itemDescription: string;
    packageWeight: number;
    paNumber: string;
    professionalAssemblyTime: number;
    predictedAssemblyTime: number;
    price: Price48;
    familyPrice: FamilyPrice11;
    totalPrice: TotalPrice32;
    totalFamilyPrice: TotalFamilyPrice22;
    imageUrl: string;
    storeInfo: StoreInfo11;
    availableStockToSell: number;
    isAssemblyRequired: boolean;
}

export interface Price48 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail105[];
    type: string;
}

export interface TaxDetail105 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface FamilyPrice11 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail106[];
    type: string;
}

export interface TaxDetail106 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalPrice32 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail107[];
    type: string;
}

export interface TaxDetail107 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalFamilyPrice22 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail108[];
    type: string;
}

export interface TaxDetail108 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface StoreInfo11 {
    isFullServe: boolean;
    isSelfServe: boolean;
    isExternalFullServe: boolean;
    handOverLocation: string;
    isAvailableInStore: boolean;
    isExternalPickingLocation: boolean;
    itemId: string;
    itemType: string;
    paNumber: string;
    pickingLocation: string;
    salesLocations: SalesLocation11[];
    salesMethodCode: string;
    salesRestrictions: SalesRestrictions11;
    stockAvailabilities: StockAvailabilities11;
    servicePrices: ServicePrice12[];
    price: Price49;
    availableStockToSell: number;
    isFoodItem: boolean;
    totalPrice: TotalPrice33;
}

export interface SalesLocation11 {
    IsHomeBase: boolean;
    IsPrimary: boolean;
    SelfServeSalesLocations: SelfServeSalesLocations11;
}

export interface SelfServeSalesLocations11 {
    Aisle: string;
    Bin: string;
}

export interface SalesRestrictions11 {
    CanSellLocal: boolean;
    CanSellRemote: boolean;
    ShouldSellLocal: boolean;
    ShouldSellRemote: boolean;
    IsOrderStopped: boolean;
    IsSaleStopped: boolean;
}

export interface StockAvailabilities11 {
    Elevated: number;
    Total: number;
    ToSell: number;
}

export interface ServicePrice12 {
    serviceProductId: string;
    currencyCode: string;
    type: string;
    priceExclTax: number;
    priceInclTax: number;
    indicativeTotalTax: number;
}

export interface Price49 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail109[];
    type: string;
    indicativeTax: number;
}

export interface TaxDetail109 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalPrice33 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail110[];
    type: string;
    indicativeTax: number;
}

export interface TaxDetail110 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface Price50 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
    taxDetails: any[];
}

export interface TotalPrice34 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
}

export interface TotalFamilyPrice23 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
}

export interface UnitPrice11 {
    changeReason: string;
    currentPrice: CurrentPrice11;
    discountedPrice: DiscountedPrice11;
    type: string;
}

export interface CurrentPrice11 {
    indicativeTax: number;
    priceExclTax: number;
    priceInclTax: number;
}

export interface DiscountedPrice11 {
    indicativeTax: number;
    priceExclTax: number;
    priceInclTax: number;
}

export interface Subtotals11 {
    prices: Price51[];
}

export interface Price51 {
    viewType: string;
    priceType: string;
    inclSavings: InclSavings18;
    exclSavings: ExclSavings18;
}

export interface InclSavings18 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail111[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail111 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface ExclSavings18 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail112[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail112 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface SelectedCartItem4 {
    itemType: string;
    itemNo: string;
    itemName: string;
    id: number;
    quantity: number;
    itemUnitCode: string;
    refId: number;
    soSalesItemNo: number;
    orderLineId: number;
    retailItem: RetailItem12;
    price: Price54;
    totalPrice: TotalPrice37;
    totalFamilyPrice: TotalFamilyPrice25;
    childItems: any[];
    isCombination: boolean;
    itemId: string;
    isAssemblyRequired: boolean;
    handedOverToTdDesk: boolean;
    isGroup: boolean;
    isTextLine: boolean;
    availableStockToSell: number;
    isProduct: boolean;
    name: string;
    isMetric: boolean;
    numberOfPackages: number;
    itemDescription: string;
    packageWeight: number;
    paNumber: string;
    professionalAssemblyTime: number;
    predictedAssemblyTime: number;
    imageUrl: string;
    totalQuantity: number;
    canCreateOrder: boolean;
    parentId: any;
    seqNo: number;
    unitPrice: UnitPrice12[];
    subtotals: Subtotals12;
    isExternalPickingLocation: boolean;
    isLocked?: boolean;
    unitofMeasure: string;
    lineId: number;
}

export interface RetailItem12 {
    name: string;
    itemId: string;
    itemType: string;
    itemUnitCode: string;
    isMetric: boolean;
    numberOfPackages: number;
    itemDescription: string;
    packageWeight: number;
    paNumber: string;
    professionalAssemblyTime: number;
    predictedAssemblyTime: number;
    price: Price52;
    familyPrice: FamilyPrice12;
    totalPrice: TotalPrice35;
    totalFamilyPrice: TotalFamilyPrice24;
    imageUrl: string;
    storeInfo: StoreInfo12;
    availableStockToSell: number;
    isAssemblyRequired: boolean;
}

export interface Price52 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail113[];
    type: string;
}

export interface TaxDetail113 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface FamilyPrice12 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail114[];
    type: string;
}

export interface TaxDetail114 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalPrice35 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail115[];
    type: string;
}

export interface TaxDetail115 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalFamilyPrice24 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail116[];
    type: string;
}

export interface TaxDetail116 {
    taxCode: number;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface StoreInfo12 {
    isFullServe: boolean;
    isSelfServe: boolean;
    isExternalFullServe: boolean;
    handOverLocation: string;
    isAvailableInStore: boolean;
    isExternalPickingLocation: boolean;
    itemId: string;
    itemType: string;
    paNumber: string;
    pickingLocation: string;
    salesLocations: SalesLocation12[];
    salesMethodCode: string;
    salesRestrictions: SalesRestrictions12;
    stockAvailabilities: StockAvailabilities12;
    servicePrices: ServicePrice13[];
    price: Price53;
    availableStockToSell: number;
    isFoodItem: boolean;
    totalPrice: TotalPrice36;
}

export interface SalesLocation12 {
    IsHomeBase: boolean;
    IsPrimary: boolean;
    SelfServeSalesLocations: SelfServeSalesLocations12;
}

export interface SelfServeSalesLocations12 {
    Aisle: string;
    Bin: string;
}

export interface SalesRestrictions12 {
    CanSellLocal: boolean;
    CanSellRemote: boolean;
    ShouldSellLocal: boolean;
    ShouldSellRemote: boolean;
    IsOrderStopped: boolean;
    IsSaleStopped: boolean;
}

export interface StockAvailabilities12 {
    Elevated: number;
    Total: number;
    ToSell: number;
}

export interface ServicePrice13 {
    serviceProductId: string;
    currencyCode: string;
    type: string;
    priceExclTax: number;
    priceInclTax: number;
    indicativeTotalTax: number;
}

export interface Price53 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail117[];
    type: string;
    indicativeTax: number;
}

export interface TaxDetail117 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface TotalPrice36 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    taxDetails: TaxDetail118[];
    type: string;
    indicativeTax: number;
}

export interface TaxDetail118 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
}

export interface Price54 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
    taxDetails: any[];
}

export interface TotalPrice37 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
}

export interface TotalFamilyPrice25 {
    currencyCode: string;
    priceInclTax: number;
    priceExclTax: number;
    type: string;
}

export interface UnitPrice12 {
    changeReason: string;
    currentPrice: CurrentPrice12;
    discountedPrice: DiscountedPrice12;
    type: string;
}

export interface CurrentPrice12 {
    indicativeTax: number;
    priceExclTax: number;
    priceInclTax: number;
}

export interface DiscountedPrice12 {
    indicativeTax: number;
    priceExclTax: number;
    priceInclTax: number;
}

export interface Subtotals12 {
    prices: Price55[];
}

export interface Price55 {
    viewType: string;
    priceType: string;
    inclSavings: InclSavings19;
    exclSavings: ExclSavings19;
}

export interface InclSavings19 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail119[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail119 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface ExclSavings19 {
    priceExclTax: number;
    priceInclTax: number;
    indicativeTax: number;
    taxDetails: TaxDetail120[];
    currencyCode: string;
    taxAmount: number;
}

export interface TaxDetail120 {
    taxCode: string;
    taxType: string;
    taxPercent: number;
    taxableAmount: number;
    taxAmount: number;
    taxPercentage: number;
}

export interface ServiceConfiguration4 {
    basePrice: number;
    basePriceType: string;
    unitPrice: number;
    currencyCode: string;
}

export interface Error2 {}

export interface Price56 {
    amount?: number;
    priceInclTax?: number;
    priceExclTax?: number;
    currencyCode?: string;
    taxDetails?: any[];
    discountPrice?: number;
}
export interface SalesLocation13 {
    IsHomeBase?: boolean;
    IsPrimary?: boolean;
    SatelliteSalesLocation?: SateliteSalesLocation;
}

export interface SateliteSalesLocation {
    Department?: string;
}
